import { useQuery } from "@tanstack/react-query";

// Starbucks coffee price in USD
const COFFEE_PRICE_USD = 2.95;

interface CoinGeckoResponse {
  bitcoin: {
    usd: number;
  };
}

/**
 * Fetches the current Bitcoin price from CoinGecko and calculates
 * the sats equivalent of a coffee ($2.95).
 * 
 * The price is cached for the session (staleTime: Infinity) and only
 * refetched on page reload or explicit invalidation.
 */
export function useCoffeePrice() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["bitcoin-price"],
    queryFn: async ({ signal }) => {
      const response = await fetch(
        "https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd",
        { signal }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch Bitcoin price");
      }

      const data: CoinGeckoResponse = await response.json();
      return data.bitcoin.usd;
    },
    staleTime: Infinity, // Cache for the entire session
    gcTime: Infinity, // Never garbage collect
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Calculate sats per coffee
  // 1 BTC = 100,000,000 sats
  // sats = (USD / BTC_price) * 100,000,000
  const btcPrice = data ?? 0;
  const coffeePriceSats = btcPrice > 0 
    ? Math.round((COFFEE_PRICE_USD / btcPrice) * 100_000_000)
    : 0;

  return {
    /** Bitcoin price in USD */
    btcPrice,
    /** Coffee price in USD */
    coffeePriceUsd: COFFEE_PRICE_USD,
    /** Coffee price in sats (calculated from current BTC price) */
    coffeePriceSats,
    /** Whether the price is still loading */
    isLoading,
    /** Any error that occurred during fetch */
    error,
    /** Refetch the Bitcoin price */
    refetch,
  };
}
